import { useCallback, useEffect, useState } from "react";
import type { FormEvent } from "react";
import type { KaneoDecisionReadItem, KaneoDecisionReadPage, KaneoEffectReadItem, KaneoEffectReadPage, KaneoEffectType, KaneoUnresolvedEffectStatus } from "../shared/contracts.js";

const emptyCounts: KaneoEffectReadPage["statusCounts"] = { indeterminate: 0, leased: 0, retryable: 0, queued: 0 };
const effectLabels: Record<KaneoEffectType, string> = {
  kaneo_comment: "Commentaire Kaneo",
  kaneo_status: "Statut Kaneo",
  kaneo_label: "Label Kaneo",
  git_push: "Publication Git",
  github_pull_request: "Pull request GitHub",
};
const statusLabels: Record<KaneoUnresolvedEffectStatus, string> = {
  indeterminate: "Indéterminé",
  leased: "Pris par le dispatcher",
  retryable: "Réessayable",
  queued: "En attente",
};

function effectDate(value: string): string {
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date.toLocaleString("fr-FR") : "Date indisponible";
}

function EffectRow({ effect, onResolved }: { effect: KaneoEffectReadItem; onResolved: () => void }) {
  const detailCode = effect.errorCode ?? effect.resolutionCode;
  const [expanded, setExpanded] = useState(false);
  const [outcome, setOutcome] = useState<"applied" | "not_applied">("applied");
  const [reasonCode, setReasonCode] = useState("");
  const [externalReference, setExternalReference] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const response = await fetch(`/api/control/kaneo/effects/${encodeURIComponent(effect.effectId)}/reconcile`, {
        method: "POST", headers: { "content-type": "application/json" },
        body: JSON.stringify({ outcome, reasonCode, ...(outcome === "applied" && externalReference ? { externalReference } : {}) }),
      });
      const result = await response.json().catch(() => ({})) as { error?: unknown; status?: unknown };
      if (!response.ok) throw new Error(typeof result.error === "string" ? result.error : "Réconciliation refusée.");
      setExpanded(false);
      setMessage(outcome === "applied" ? "Effet confirmé comme appliqué et clôturé." : "Effet déclaré non appliqué; il est réessayable si le dispatcher est activé.");
      onResolved();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Réconciliation indisponible.");
    } finally {
      setBusy(false);
    }
  }

  return <tr>
    <td><span className={`effect-status effect-status-${effect.status}`}>{statusLabels[effect.status]}</span></td>
    <td>{effectLabels[effect.effectType]}</td>
    <td className="effect-scope"><code>{effect.taskId}</code><small>Projet <code>{effect.projectId}</code> · workspace <code>{effect.workspaceId}</code></small></td>
    <td className="effect-run"><code>{effect.runId ?? "—"}</code></td>
    <td className="mono">{effect.attempt}/{effect.maxAttempts}</td>
    <td><time dateTime={effect.updatedAt}>{effectDate(effect.updatedAt)}</time>{detailCode && <small className="effect-error-code">{detailCode}</small>}{message && <small role="status">{message}</small>}</td>
    <td className="effect-id"><code>{effect.effectId}</code>{effect.status === "indeterminate" && <div className="effect-reconcile">
      {!expanded ? <button type="button" className="pagination-button" onClick={() => setExpanded(true)}>Réconcilier</button> : <form onSubmit={(event) => void submit(event)}>
        <label>Verdict<select value={outcome} onChange={(event) => setOutcome(event.target.value as "applied" | "not_applied")} disabled={busy}>
          <option value="applied">Effet appliqué</option><option value="not_applied">Effet non appliqué</option>
        </select></label>
        <label>Code du motif<input required pattern="[A-Za-z][A-Za-z0-9_]{0,99}" maxLength={100} value={reasonCode} onChange={(event) => setReasonCode(event.target.value)} disabled={busy} /></label>
        {outcome === "applied" && <label>Référence externe (facultative)<input maxLength={500} value={externalReference} onChange={(event) => setExternalReference(event.target.value)} disabled={busy} /></label>}
        <label className="effect-reconcile-confirm"><input type="checkbox" required disabled={busy} />Je confirme ce verdict opérateur.</label>
        {error && <small className="alert alert-error" role="alert">{error}</small>}
        <div><button type="submit" className="pagination-button" disabled={busy}>{busy ? "Enregistrement…" : "Enregistrer le verdict"}</button> <button type="button" className="pagination-button" disabled={busy} onClick={() => setExpanded(false)}>Annuler</button></div>
      </form>}
    </div>}</td>
  </tr>;
}

export function KaneoEffectsPanel() {
  const [page, setPage] = useState(1);
  const [data, setData] = useState<KaneoEffectReadPage>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setError("");
    setData(undefined);
    try {
      const response = await fetch(`/api/control/kaneo/effects?page=${page}`, { signal, cache: "no-store" });
      if (!response.ok) {
        const body = await response.json().catch(() => ({})) as { error?: unknown };
        throw new Error(typeof body.error === "string" ? body.error : "Lecture des effets indisponible.");
      }
      const result = await response.json() as KaneoEffectReadPage;
      if (!signal?.aborted) setData(result);
    } catch (cause) {
      if (!signal?.aborted) setError(cause instanceof Error ? cause.message : "Lecture des effets indisponible.");
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const counts = data?.statusCounts ?? emptyCounts;
  const announcement = loading
    ? "Chargement des effets Kaneo."
    : error
      ? error
      : data
        ? `${data.total} effet${data.total === 1 ? "" : "s"} non résolu${data.total === 1 ? "" : "s"}. Page ${data.page}.`
        : "";
  return <><section className="panel kaneo-effects" aria-labelledby="kaneo-effects-title" aria-busy={loading}>
    <header className="kaneo-effects-header">
      <div className="kaneo-effects-title-row">
        <div><h2 id="kaneo-effects-title">Effets Kaneo</h2><p className="muted">Registre des publications externes encore non résolues.</p></div>
        <button type="button" className="pagination-button" disabled={loading} onClick={() => void load()}>{loading ? "Actualisation…" : "Actualiser"}</button>
      </div>
      <div className="effect-readonly-notice" role="note"><strong>Réconciliation opérateur.</strong> Seuls les effets indéterminés peuvent recevoir un verdict. « Non appliqué » peut rendre l’effet réessayable si le dispatcher est activé; aucun appel fournisseur n’est lancé par cette action. Le texte des commentaires et les payloads ne sont pas affichés; l’audit est attribué au compte partagé.</div>
      <div className="effect-counts" role="group" aria-label="Répartition des effets non résolus">
        <div className="effect-count effect-count-indeterminate"><strong>{counts.indeterminate}</strong><span>Indéterminés · à examiner d’abord</span></div>
        <div className="effect-count"><strong>{counts.leased}</strong><span>Pris par le dispatcher</span></div>
        <div className="effect-count"><strong>{counts.retryable}</strong><span>Réessayables</span></div>
        <div className="effect-count"><strong>{counts.queued}</strong><span>En attente</span></div>
        <div className="effect-count effect-count-total"><strong>{data?.total ?? "—"}</strong><span>Non résolus au total</span></div>
      </div>
    </header>
    <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">{announcement}</div>
    {error && <p className="alert alert-error effect-source-error">{error}</p>}
    {loading && <div className="effect-loading" aria-hidden="true"><span /> <span /> <span />Chargement du registre…</div>}
    {!loading && !error && data && data.effects.length === 0 && <div className="empty-state effect-empty"><span className="empty-glyph" aria-hidden="true">✓</span><strong>Aucun effet non résolu</strong><span>La source PostgreSQL a répondu ; aucun effet en attente, loué, réessayable ou indéterminé.</span></div>}
    {!loading && !error && data && data.effects.length > 0 && <>
      <div className="table-wrap effect-table-wrap"><table>
        <caption>Effets non résolus — {data.total} au total, indéterminés affichés en premier · page {data.page}</caption>
        <thead><tr><th scope="col">État</th><th scope="col">Effet</th><th scope="col">Ticket et périmètre</th><th scope="col">Run</th><th scope="col">Tentative</th><th scope="col">Dernier changement</th><th scope="col">Référence et opération</th></tr></thead>
        <tbody>{data.effects.map((effect) => <EffectRow effect={effect} key={effect.effectId} onResolved={() => void load()} />)}</tbody>
      </table></div>
      <nav className="pagination effect-pagination" aria-label="Pagination des effets Kaneo">
        <span>{data.total} effet{data.total === 1 ? "" : "s"} non résolu{data.total === 1 ? "" : "s"}</span>
        <div className="pagination-controls"><button type="button" className="pagination-button" disabled={data.page <= 1 || loading} onClick={() => setPage((current) => Math.max(1, current - 1))}>Précédente</button><span className="pagination-page">Page {data.page}</span><button type="button" className="pagination-button" disabled={!data.hasMore || loading} onClick={() => setPage((current) => current + 1)}>Suivante</button></div>
      </nav>
    </>}
  </section><KaneoDecisionHistory /></>;
}

const decisionLabels: Record<KaneoDecisionReadItem["resultKind"], string> = {
  no_action: "Aucune action",
  request_human: "Intervention humaine demandée",
  ignored: "Ignorée",
  refused: "Refusée",
};

function KaneoDecisionHistory() {
  const [page, setPage] = useState(1);
  const [data, setData] = useState<KaneoDecisionReadPage>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setError("");
    setData(undefined);
    try {
      const response = await fetch(`/api/control/kaneo/decisions?page=${page}`, { signal, cache: "no-store" });
      if (!response.ok) {
        const body = await response.json().catch(() => ({})) as { error?: unknown };
        throw new Error(typeof body.error === "string" ? body.error : "Lecture des décisions indisponible.");
      }
      const result = await response.json() as KaneoDecisionReadPage;
      if (!signal?.aborted) setData(result);
    } catch (cause) {
      if (!signal?.aborted) setError(cause instanceof Error ? cause.message : "Lecture des décisions indisponible.");
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const announcement = loading ? "Chargement de l’historique des décisions Kaneo."
    : error ? error
      : data ? `${data.total} décision${data.total === 1 ? "" : "s"} persistée${data.total === 1 ? "" : "s"}. Page ${data.page}.` : "";

  return <section className="panel kaneo-decision-history" aria-labelledby="kaneo-decision-history-title" aria-busy={loading}>
    <header className="kaneo-effects-header"><div className="kaneo-effects-title-row">
      <div><h2 id="kaneo-decision-history-title">Historique des décisions Kaneo</h2><p className="muted">Résultats structurés persistés; les prompts, réponses libres et payloads ne sont pas exposés.</p></div>
      <button type="button" className="pagination-button" disabled={loading} onClick={() => void load()}>{loading ? "Actualisation…" : "Actualiser"}</button>
    </div></header>
    <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">{announcement}</div>
    {error && <p className="alert alert-error" role="alert">{error}</p>}
    {loading && <div className="effect-loading" aria-hidden="true"><span /> <span /> <span />Chargement de l’historique…</div>}
    {!loading && !error && data && data.decisions.length === 0 && <div className="empty-state effect-empty"><span className="empty-glyph" aria-hidden="true">✓</span><strong>Aucune décision persistée</strong><span>La source PostgreSQL a répondu; aucune décision structurée n’est enregistrée.</span></div>}
    {!loading && !error && data && data.decisions.length > 0 && <>
      <div className="table-wrap kaneo-decision-table-wrap"><table>
        <caption>Décisions persistées — {data.total} au total · page {data.page}</caption>
        <thead><tr><th scope="col">Date</th><th scope="col">Résultat</th><th scope="col">Événement</th><th scope="col">Ticket et périmètre</th><th scope="col">Politique</th><th scope="col">Motif</th></tr></thead>
        <tbody>{data.decisions.map((decision, index) => <tr key={`${decision.decidedAt}:${decision.taskId}:${index}`}>
          <td><time dateTime={decision.decidedAt}>{effectDate(decision.decidedAt)}</time></td>
          <td>{decisionLabels[decision.resultKind]}</td>
          <td><code>{decision.eventType}</code></td>
          <td className="effect-scope"><code>{decision.taskId}</code><small>Projet <code>{decision.projectId}</code> · workspace <code>{decision.workspaceId}</code></small></td>
          <td><code>{decision.policyVersion}</code></td>
          <td>{decision.reasonCode ? <code>{decision.reasonCode}</code> : "—"}</td>
        </tr>)}</tbody>
      </table></div>
      <nav className="pagination" aria-label="Pagination des décisions Kaneo">
        <span>{data.total} décision{data.total === 1 ? "" : "s"}</span>
        <div className="pagination-controls"><button type="button" className="pagination-button" disabled={data.page <= 1 || loading} onClick={() => setPage((current) => Math.max(1, current - 1))}>Précédente</button><span className="pagination-page">Page {data.page}</span><button type="button" className="pagination-button" disabled={!data.hasMore || loading} onClick={() => setPage((current) => current + 1)}>Suivante</button></div>
      </nav>
    </>}
  </section>;
}
